/**
 * #650 — the trader's own resting orders at one ladder level, bounded.
 *
 * Every ladder drew one chip per own order in an unbounded horizontal flex
 * row. Measured on the real board at 1470x956, one rung's chip group ran
 * **64px at 1 order → 141 → 339 → 537px at 10**, and it grows by TAKING:
 * `TwoSidedLadder`'s price column is `minmax(0,1fr)`, so it shrinks to make
 * room. The price text's right edge walked 500px → 423px → 225px → **27px**
 * across that ramp, and at the top of it every chip lost its queue reading to
 * its own `truncate` while the odds and resting size overprinted each other.
 * Nothing on screen said any of it had happened.
 *
 * So the group is now bounded: at most `INLINE_CHIP_LIMIT` chips inline, and
 * when there are more, a trailing `+N` opens a popover listing every order —
 * each one still its own chip, still its own cancel.
 *
 * ─── Why a fixed count, not "as many as fit" ─────────────────────────────────
 *
 * Fitting is the thing the ticket describes, but measuring it means reading
 * layout during render and re-deciding on every resize and every quote tick —
 * on a surface whose whole complaint is that it moves. A fixed cap is decided
 * before layout, so the group's width depends only on the count, and the count
 * only changes when orders actually arrive or leave. The cap is 2 because 2 is
 * the widest the group gets while the row still renders cleanly at the
 * narrowest supported viewport (141px of chips, price column still 423px); by
 * 5 the price column is down to 225px.
 *
 * ⚠️ **A level is a PROP, and it changes under a mounted component.** The two
 * table ladders key their rows by INDEX — `GameTable` by
 * `${game.key}-${teamSide}-${kind}-${idx}` and `SpreadsTotalsTable` by
 * `ask-${idx}` / `bid-${idx}` — so one new best bid, routine, hands this same
 * instance a different price. An open popover that survived that would be
 * listing, and offering to cancel, orders from a rung the row no longer draws.
 * (`TwoSidedLadder` keys by `${kind}-${cents}` and so remounts instead; the
 * guard below must still hold there, because that is its row key's doing and
 * not this component's.)
 * The open state is therefore dropped the moment the level identity changes,
 * using the same adjust-during-render pattern `CancelLevelControl` documents
 * (an effect runs a tick late, leaving one frame of a stale-but-clickable
 * list). This is the single most important thing in this file.
 *
 * Keyboard: the chips were `<span role="button">` with an `onClick` and nothing
 * else — not focusable, not operable. They are focusable here, but they stay
 * spans rather than becoming `<button>`s on purpose: `Lt2HotkeyListener` treats
 * a bare `<button>` as a keystroke OWNER, so promoting them would silently
 * refuse the A/D one-click hotkeys whenever a chip held focus. The `+N`
 * trigger IS a button — it opens a dialog, and swallowing A/D while an
 * overflow list is open is the safe direction.
 */
import { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { cn } from '@/lib/infra/utils';
import { useAccountStore } from '@/stores/accountStore';
import { isKalshiCancelPending } from '@/lib/trading/kalshiCancelLifecycle';
import { useDismissibleLayer } from '@/hooks/ui/useDismissibleLayer';
import { RestingOrderChip } from './RestingOrderChip';
import { CancelLevelControl, type LadderLevelIdentity } from './CancelLevelControl';
import type { BidRowOrder } from './helpers';

/**
 * How many chips render inline before the rest move into the popover.
 *
 * Raising this is one line, and it is the knob to turn if the desk would
 * rather see more at a glance — but it is bounded by the narrowest supported
 * viewport, not by taste: see the measurements in the file header.
 */
export const INLINE_CHIP_LIMIT = 2;

/** Panel geometry. Fixed width so the position can be computed before layout
 *  rather than measured after it — the ladder repaints ~4x/s. */
const PANEL_WIDTH = 168;
const VIEWPORT_MARGIN = 8;
const MIN_PANEL_HEIGHT = 80;
/** Content geometry, so the panel can be PLACED before it is measured: the
 *  ladder repaints ~4x/s and a measure-then-move would be visible as a jump. */
const PANEL_CHROME = 28; // padding + the "N resting @ X¢" heading
const PANEL_ROW = 20; // one chip row at the pinned leading-4

/**
 * Everything that distinguishes one rung from another, as one value. Equality
 * of this string is what "the row still means what it meant" means.
 *
 * `priceCents` is folded in separately because a surface may supply the price
 * WITHOUT a level (TwoSidedLadder passes `level={null}`). Keyed on the level
 * alone this returned `''` for every rung there, so the stale-rung guard was
 * inert on that surface and only its `${kind}-${cents}` row key kept it safe —
 * safety by accident, which the next index-keyed caller would not inherit.
 */
function rungKey(level: LadderLevelIdentity | null, priceCents: number | null): string {
  if (level) {
    return `${level.marketTicker}|${level.side}|${level.action}|${level.priceCents}`;
  }
  return priceCents != null ? `|||${priceCents}` : '';
}

/** The hairline between two chips. Shared so the inline row and the popover
 *  cannot drift into two different separators for the same relationship. */
function ChipSeparator() {
  return <span className="bg-muted-foreground/40 h-3 w-px shrink-0 self-center" />;
}

export function RestingOrderGroup({
  orders,
  level,
  priceCents,
}: {
  orders: readonly BidRowOrder[];
  /** Null on surfaces that do not offer the level-wide cancel. */
  level: LadderLevelIdentity | null;
  /**
   * The rung's price, for surfaces that have one but no level-cancel control.
   * Separate from `level` on purpose: the contract forbids truncating the
   * price-level ASSOCIATION, and a panel headed only "10 resting" has lost it
   * — but offering a destructive level-wide cancel where none existed before
   * is not this ticket's to add, so the two cannot be the same prop.
   */
  priceCents?: number | null;
}) {
  const [open, setOpen] = useState(false);
  const triggerRef = useRef<HTMLButtonElement>(null);
  const panelRef = useRef<HTMLDivElement>(null);
  // The whole map, as CancelLevelControl reads it: this control cares about N
  // orders at once, and every lifecycle transition returns the map BY IDENTITY
  // when nothing changed, so a quiet poll cannot re-render the ladder.
  const cancelStates = useAccountStore((s) => s.kalshiCancelStates);

  const rungCents = level?.priceCents ?? priceCents ?? null;
  const priceLabel = rungCents != null ? `${rungCents}¢` : '';

  const key = rungKey(level, rungCents);
  const [seenKey, setSeenKey] = useState(key);
  // React's adjust-state-during-render pattern, NOT an effect — see the header.
  if (seenKey !== key) {
    setSeenKey(key);
    if (open) setOpen(false);
  }

  // ⚠️ An open panel must not survive its own disclosure disappearing.
  // Orders fill and cancel constantly: a trader who opens `+8`, watches the
  // level drain below the inline cap, then places more would otherwise have
  // the panel spring back open on its own — an unrequested overlay of cancel
  // controls over a live book. Held here rather than in an effect for the same
  // reason as the rung change: an effect leaves one frame in which the stale
  // panel is on screen AND clickable.
  const overflowCount = Math.max(0, orders.length - INLINE_CHIP_LIMIT);
  if (open && overflowCount === 0) setOpen(false);

  /**
   * ⚠️ The panel is PORTALLED, and that is not cosmetic.
   *
   * Both table ladders wrap this group in `overflow-hidden` (GameTable and
   * SpreadsTotalsTable — it is what stops the own-order column from widening
   * the table). An absolutely-positioned panel inside that wrapper is clipped
   * by it, so the expanded list could be cut off exactly where a trader needs
   * to read it. Rendering to `document.body` escapes every clipping ancestor
   * without touching the wrapper that the column bound depends on.
   *
   * `fixed` + explicit coordinates rather than `absolute`, for the same reason
   * `AlertControlsMenu` does it: the ladder lives inside a horizontally
   * scrolling container, so the panel has to be re-anchored on scroll instead
   * of riding a positioned ancestor.
   */
  const [pos, setPos] = useState({ top: 0, left: 0, maxHeight: 0 });
  /** How many rows the panel will draw — the overflow, not the whole set. */
  const hiddenRows = Math.max(0, orders.length - INLINE_CHIP_LIMIT);

  const reposition = useCallback(() => {
    const rect = triggerRef.current?.getBoundingClientRect();
    if (!rect) return;
    // Right-align to the trigger, then clamp so it can never leave the viewport
    // on a narrow board — the failure this whole ticket is about.
    const rightLimit = Math.max(
      VIEWPORT_MARGIN,
      window.innerWidth - PANEL_WIDTH - VIEWPORT_MARGIN,
    );
    const left = Math.min(Math.max(VIEWPORT_MARGIN, rect.right - PANEL_WIDTH), rightLimit);

    // Ladder rungs sit low on the board as often as not, so "always below" put
    // the tail of the list under the fold — visible only by scrolling, which
    // is not the same as visible. Prefer below, FLIP ABOVE when the whole list
    // fits there and would not fit below, and only clamp-and-scroll when it
    // fits in neither.
    const wanted = PANEL_CHROME + hiddenRows * PANEL_ROW;
    const roomBelow = window.innerHeight - rect.bottom - 4 - VIEWPORT_MARGIN;
    const roomAbove = rect.top - 4 - VIEWPORT_MARGIN;

    let top: number;
    if (wanted <= roomBelow || roomBelow >= roomAbove) {
      top = Math.max(VIEWPORT_MARGIN, rect.bottom + 4);
    } else {
      top = Math.max(VIEWPORT_MARGIN, rect.top - 4 - Math.min(wanted, roomAbove));
    }
    if (window.innerHeight - top - VIEWPORT_MARGIN < MIN_PANEL_HEIGHT) {
      top = Math.max(VIEWPORT_MARGIN, window.innerHeight - VIEWPORT_MARGIN - MIN_PANEL_HEIGHT);
    }
    const maxHeight = window.innerHeight - top - VIEWPORT_MARGIN;
    setPos((prev) =>
      prev.top === top && prev.left === left && prev.maxHeight === maxHeight
        ? prev
        : { top, left, maxHeight },
    );
  }, [hiddenRows]);

  useLayoutEffect(() => {
    if (open) reposition();
  }, [open, reposition]);

  useEffect(() => {
    if (!open) return;
    // Capture phase: the scrolling element is the table's own overflow-x
    // container, not the window.
    window.addEventListener('scroll', reposition, true);
    window.addEventListener('resize', reposition);
    return () => {
      window.removeEventListener('scroll', reposition, true);
      window.removeEventListener('resize', reposition);
    };
  }, [open, reposition]);

  /** Close, and put focus somewhere real. A keyboard user who pressed Escape
   *  while inside the panel would otherwise be dropped on <body> — the panel
   *  that held their focus unmounts — losing their place in the ladder
   *  entirely. Only reclaimed when focus was actually inside; a mouse dismiss
   *  elsewhere on the page must not yank focus back here. */
  const close = () => {
    const inside = panelRef.current?.contains(document.activeElement);
    setOpen(false);
    if (inside) triggerRef.current?.focus();
  };

  useDismissibleLayer({
    open,
    onDismiss: close,
    refs: [triggerRef, panelRef],
    // Matches AlertControlsMenu: this panel is `z-[100]` while atoms/Modal is
    // `z-50`, so without focus-out dismissal it could sit ON TOP of a modal as
    // a background layer. Focus inside the panel counts as inside (both the
    // trigger and the panel are refs), so tabbing between chips is safe.
    dismissOnFocusOut: true,
  });

  // The portal appends the panel at the END of <body>, so a Tab from the
  // trigger would otherwise walk the whole rest of the page before reaching
  // the orders — which would make "every order reachable by keyboard" true
  // only in the most literal sense. Move focus into the panel on open; Escape
  // hands it back to the trigger via `close()`.
  useEffect(() => {
    if (open) panelRef.current?.focus();
  }, [open]);

  if (orders.length === 0) return null;

  const inline = orders.slice(0, INLINE_CHIP_LIMIT);
  const hidden = orders.slice(INLINE_CHIP_LIMIT);
  const hiddenCount = overflowCount;

  /**
   * A hidden order in a non-neutral lifecycle state is the one thing a bare
   * count cannot say. The contract forbids truncating pending/timed-out/error
   * state without a visible affordance, so the disclosure carries it: amber
   * for a cancel the venue never confirmed (the trader must act), dimmed-pulse
   * for one still in flight. The chips inside keep their own exact states.
   */
  const hiddenTimedOut = hidden.some((o) => cancelStates[o.orderId]?.phase === 'timed-out');
  const hiddenPending = hidden.some((o) => isKalshiCancelPending(cancelStates[o.orderId]));
  const stateNote = hiddenTimedOut
    ? ' — includes a cancel the venue has not confirmed'
    : hiddenPending
      ? ' — includes a cancel in flight'
      : '';

  return (
    <>
      {level && <CancelLevelControl level={level} orders={orders} />}
      {inline.map((o, i) => (
        <span key={o.orderId} className="contents">
          {i > 0 && <ChipSeparator />}
          <RestingOrderChip order={o} />
        </span>
      ))}
      {hiddenCount > 0 && (
        <span className="relative shrink-0">
          <button
            ref={triggerRef}
            type="button"
            // The count IS the disclosure: the contract forbids hiding the
            // existence of further orders without a visible count.
            aria-label={
              `Show all ${orders.length} of your resting orders` +
              `${priceLabel ? ` at ${priceLabel}` : ' at this level'}`
            }
            aria-expanded={open}
            aria-haspopup="dialog"
            title={
              `${hiddenCount} more resting ${hiddenCount === 1 ? 'order' : 'orders'}` +
              `${priceLabel ? ` at ${priceLabel}` : ' at this level'}` +
              ` — click to show all ${orders.length}${stateNote}`
            }
            onClick={(e) => {
              e.stopPropagation();
              if (open) close();
              else setOpen(true);
            }}
            className={cn(
              'text-muted-foreground hover:text-foreground shrink-0 rounded px-0.5',
              // Same pinned leading as the chips beside it — a disclosure that
              // reintroduced the 4px would undo the fix it belongs to.
              'tabular-nums leading-4 transition-colors',
              'focus-visible:ring-primary/70 focus-visible:outline-none focus-visible:ring-2',
              open && 'bg-muted/60 text-foreground',
              // A hidden order's lifecycle state has nowhere else to show.
              hiddenTimedOut && 'text-amber-400',
              !hiddenTimedOut && hiddenPending && 'animate-pulse opacity-60',
            )}
          >
            +{hiddenCount}
          </button>
          {open &&
            createPortal(
              <div
                ref={panelRef}
                // Non-modal on purpose: `Lt2HotkeyListener`'s MODAL_SELECTOR is
                // `[role="dialog"][aria-modal="true"]`, so a modal here would
                // suppress the A/D hotkeys page-wide the way the pre-#609 bare
                // `[role="dialog"]` query did. Stated explicitly rather than
                // left absent, because the portal puts this on <body> where a
                // future modal query is likelier to sweep it up.
                role="dialog"
                aria-modal="false"
                aria-label={
                  `Your ${orders.length} resting orders` +
                  `${priceLabel ? ` at ${priceLabel}` : ' at this level'}`
                }
                tabIndex={-1}
                onClick={(e) => e.stopPropagation()}
                style={{ top: pos.top, left: pos.left, maxHeight: pos.maxHeight, width: PANEL_WIDTH }}
                className={cn(
                  'bg-card border-border fixed z-[100] overflow-y-auto rounded-lg border',
                  'flex flex-col gap-0.5 p-1.5 shadow-lg',
                  'text-[10px] font-normal leading-4 focus:outline-none',
                )}
                // Width is fixed so `reposition` can place it without measuring.
                // max-w keeps it honest on a viewport narrower than the panel.
              >
                {/* The heading carries the TOTAL, so the complete set is legible
                    from the panel even though the panel only draws the part the
                    row could not. */}
                <div className="text-muted-foreground mb-1 whitespace-nowrap px-0.5 text-[9px] font-semibold uppercase tracking-wider">
                  {orders.length} resting{priceLabel ? ` @ ${priceLabel}` : ''}
                </div>
                {/* Only the overflow, NOT the whole list. Re-drawing the inline
                    chips here would put two live controls on screen for one
                    order — and because each chip owns its own arm state, arming
                    one would leave its twin looking un-armed. */}
                {hidden.map((o) => (
                  <span key={o.orderId} className="flex items-center justify-end">
                    <RestingOrderChip order={o} />
                  </span>
                ))}
              </div>,
              document.body,
            )}
        </span>
      )}
    </>
  );
}
